/**
 * ── THE CLIP LOADER — animations ONLY; the preview mesh is discarded (plan §3 `clip`) ───────────
 *
 * THE PREVIEW-MESH TRAP (research 02 §1.2): UE's glTF exporter bakes the skeletal mesh into every
 * animation export, so each of the character's eleven clip GLBs carries a 4110-vertex duplicate
 * body — 150–530 KB apiece, downloaded and parsed for nothing, because `CharacterView` reads only
 * `animations[0]`. The cook's PACK step strips it (`pack_axon.mjs` is the model); this loader is
 * the runtime half of the same rule: whatever a clip file carries beside its curves is parsed
 * (GLTFLoader has no "skip meshes" switch), COUNTED, and disposed on the spot, so a clip source
 * holds `AnimationClip`s and zero GPU bytes. `discardedMeshes > 0` is the tell that a file was
 * not packed — the service warns once with the count.
 *
 * Binding is by node name at the MIXER (three's `PropertyBinding`), against whichever body root a
 * caller hands `mixer.clipAction(clip, root)`; that is why the source is shared and never cloned.
 * ⚠️ three sanitises node names at parse (`PropertyBinding.sanitizeNodeName` strips `[ ] . : /`
 * and maps whitespace to `_`); a clip whose track names a bone the body spells differently
 * (`Rig` vs `rig` is live today) binds to nothing, silently. The pack step is where that is
 * refused; the runtime cannot tell a renamed bone from an intentional partial clip.
 */

import type * as THREE from 'three/webgpu';

import { disposeTracked, parseGltf } from './gltf';

export interface ParsedClips {
  clips: THREE.AnimationClip[];
  /** Meshes the file carried and this loader threw away — non-zero means an unpacked export. */
  discardedMeshes: number;
}

export async function parseClips(buf: ArrayBuffer, baseUrl = ''): Promise<ParsedClips> {
  const parsed = await parseGltf(buf, baseUrl);
  // The scene graph is not kept: nothing references it once the clips are lifted out, and its
  // geometries/materials/textures are released NOW rather than at the source's grace period.
  disposeTracked(parsed.tracked);
  return { clips: parsed.animations, discardedMeshes: parsed.meshes };
}
